import React, { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2, XCircle, Flag, MessageSquare, AlertTriangle, ShieldCheck,
  Copy, ChevronRight, ChevronDown, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { BrandLogo } from "@/components/BrandLogo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { format } from "@/lib/brand-tokens";
import {
  getSubmissionById, getMemberById, getDealById, getBrandById, getCardById,
  getCardsBySubmission, getAdminById, ADM08_DETAIL_SUBMISSION,
  updateCardStatus, bulkUpdateCardsStatus, updateSubmissionStatus,
} from "@/lib/mock-data";
import { Gated } from "@/components/Gated";
import { useStoreVersion } from "@/hooks/useStore";
import { toast } from "sonner";

// ADM-08 — Submission detail (exception-queue model)
//
// Design rationale: the member-side parser already validates format, length,
// PIN structure, brand prefix, and intra-paste duplicates. The auto-approve
// step in submitCards() promotes every clean card to Approved on entry and
// only leaves *exceptions* (cross-member duplicates, unresolved flags) for
// admin review. This page surfaces the exceptions first; the auto-approved
// stack is collapsed by default.
export default function SubmissionDetailPage() {
  useStoreVersion();
  const { submissionId } = useParams();
  const submission = getSubmissionById(submissionId) || ADM08_DETAIL_SUBMISSION;
  const member = getMemberById(submission.memberId);
  const deal = getDealById(submission.dealId);
  const brand = getBrandById(submission.brandId);
  const cards = getCardsBySubmission(submission.id);

  const FLAG_STATUSES = new Set(["FlaggedFormat", "FlaggedDuplicate", "FlaggedAmount", "Pending", "AwaitingMemberResponse"]);
  const flaggedCards   = cards.filter((c) => FLAG_STATUSES.has(c.status));
  const approvedCards  = cards.filter((c) => c.status === "Approved");
  const rejectedCards  = cards.filter((c) => c.status === "Rejected");

  const totalApprovedFace = approvedCards.reduce((a, c) => a + c.faceValue, 0);
  const flaggedFace = flaggedCards.reduce((a, c) => a + c.faceValue, 0);

  // Action bar bulk operations apply to the *flagged set only* — clean cards
  // were already approved at submission time. The submission's status is
  // recomputed automatically by the mutators (see recomputeSubmissionStatus).
  const approveAllFlagged = () => {
    if (flaggedCards.length === 0) {
      toast.info("Nothing to review", { description: "All cards in this submission are clean." });
      return;
    }
    bulkUpdateCardsStatus(flaggedCards.map((c) => c.id), "Approved");
    toast.success(`Approved ${flaggedCards.length} flagged card${flaggedCards.length === 1 ? "" : "s"}`, {
      description: "Submission closed and override decisions logged.",
    });
  };
  const rejectAllFlagged = () => {
    if (flaggedCards.length === 0) {
      toast.info("Nothing to reject", { description: "No flagged cards remain." });
      return;
    }
    bulkUpdateCardsStatus(flaggedCards.map((c) => c.id), "Rejected");
    toast.success(`Rejected ${flaggedCards.length} flagged card${flaggedCards.length === 1 ? "" : "s"}`);
  };

  const [responseOpen, setResponseOpen] = useState(false);
  const [responseText, setResponseText] = useState("");
  const sendResponse = () => {
    if (!responseText.trim()) return;
    updateSubmissionStatus(submission.id, "AwaitingMemberResponse", responseText.slice(0, 200));
    toast.success("Response request sent", { description: "Member will be notified by email." });
    setResponseOpen(false); setResponseText("");
  };

  // Per-row override dialog (Approve anyway / Reject with reason)
  const [override, setOverride] = useState(null); // { cardId, kind: "approve"|"reject", suggestedReason }
  const [overrideReason, setOverrideReason] = useState("");
  const openOverride = (cardId, kind, suggested) => {
    setOverrideReason(suggested || "");
    setOverride({ cardId, kind });
  };
  const confirmOverride = () => {
    if (!override) return;
    const { cardId, kind } = override;
    const reason = overrideReason.trim() || (kind === "approve" ? "Operator override" : "Operator rejection");
    updateCardStatus(cardId, kind === "approve" ? "Approved" : "Rejected", { reason });
    toast.success(kind === "approve" ? "Card approved" : "Card rejected", { description: reason });
    setOverride(null); setOverrideReason("");
  };

  // Auto-approved table: search + collapse
  const [autoExpanded, setAutoExpanded] = useState(false);
  const [autoQ, setAutoQ] = useState("");
  const filteredAuto = useMemo(() => approvedCards.filter(c => {
    if (!autoQ) return true;
    return String(c.cardNumber).includes(autoQ) || String(c.faceValue).includes(autoQ);
  }), [approvedCards, autoQ]);

  return (
    <div data-testid="submission-detail-page" className="space-y-5 pb-10">
      <Breadcrumbs items={[
        { label: "Admin", to: "/admin/dashboard" },
        { label: "Submissions", to: "/admin/submissions" },
        { label: submission.id },
      ]} />

      {/* STICKY action bar — survives scrolling through 200+ rows */}
      <div className="sticky top-14 z-10 -mx-8 px-8 py-3 bg-surface-alt/95 backdrop-blur border-b border-line">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandLogo brand={brand} size="md" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-brand text-h2 text-ink font-semibold truncate">Submission {submission.id}</h1>
                <StatusBadge entity="submission" status={submission.status} dot />
                {(submission.status === "Completed" || submission.status === "Rejected") && submission.reviewedBy && (
                  <span data-testid="reviewed-by-stamp" className="inline-flex items-center gap-1 text-caption text-ink-muted bg-surface border border-line rounded-full px-2 py-0.5">
                    <ShieldCheck className="size-3 text-emerald-600" />
                    Reviewed by {getAdminById(submission.reviewedBy)?.firstName || "Admin"}
                    {submission.reviewedAt && <> · {format.relative(submission.reviewedAt)}</>}
                  </span>
                )}
              </div>
              <div className="text-caption text-ink-subtle">
                {member?.firstName} {member?.lastName} · {deal?.name} · {brand?.name}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-caption text-ink-muted mr-1">
              <span className="text-orange-700 font-semibold tabular-nums">{flaggedCards.length}</span> need review ·
              <span className="text-emerald-700 font-semibold tabular-nums ml-1">{approvedCards.length}</span> clean
            </span>
            <Gated action="submissions.request">
              <Button variant="outline" size="sm" onClick={() => setResponseOpen(true)} data-testid="request-response-btn">
                <MessageSquare className="size-3.5 mr-1.5" /> Request response
              </Button>
            </Gated>
            <Gated action="submissions.reject">
              <Button variant="outline" size="sm" onClick={rejectAllFlagged}
                      disabled={flaggedCards.length === 0}
                      className="border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50" data-testid="reject-all-btn">
                <XCircle className="size-3.5 mr-1.5" /> Reject flagged
              </Button>
            </Gated>
            <Gated action="submissions.approve">
              <Button size="sm" onClick={approveAllFlagged}
                      disabled={flaggedCards.length === 0}
                      className="bg-brand-700 hover:bg-brand-800 text-white disabled:opacity-50" data-testid="approve-all-btn">
                <CheckCircle2 className="size-3.5 mr-1.5" /> Approve flagged
              </Button>
            </Gated>
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Mini label="Total cards"        value={cards.length} testid="mini-total" />
        <Mini label="Auto-approved"      value={approvedCards.length} accent="success" testid="mini-approved" />
        <Mini label="Needs review"       value={flaggedCards.length} accent="flagged" testid="mini-flagged" />
        <Mini label="Approved face value" value={format.currency(totalApprovedFace)} testid="mini-face" />
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* MAIN: exception queue */}
        <section className="lg:col-span-9 space-y-5">
          {/* Flagged exception queue */}
          {flaggedCards.length === 0 ? (
            <div data-testid="all-clean-banner" className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 flex items-start gap-4">
              <ShieldCheck className="size-6 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-h3 font-semibold text-emerald-900">All cards passed the parser checks.</h3>
                <p className="mt-1 text-body-sm text-ink-muted leading-relaxed">
                  No duplicates across submissions, no format issues, no flagged amounts. The {approvedCards.length} card{approvedCards.length === 1 ? "" : "s"} below were auto-approved at submission time. No admin action required.
                </p>
              </div>
            </div>
          ) : (
            <div data-testid="flagged-section" className="bg-surface border border-line rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-line flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-brand text-h3 text-ink font-semibold flex items-center gap-2">
                    <AlertTriangle className="size-4 text-orange-600" /> {flaggedCards.length} card{flaggedCards.length === 1 ? "" : "s"} need your review
                  </h2>
                  <p className="text-caption text-ink-subtle mt-0.5">
                    {format.currency(flaggedFace)} face value awaiting decision. Click any row to inspect why it was flagged.
                  </p>
                </div>
                <span className="text-caption text-ink-muted">Tip: bulk-approve unflagged via the sticky bar above.</span>
              </div>
              <div className="divide-y divide-line/60" data-testid="flagged-rows">
                {flaggedCards.map((c, i) => (
                  <FlaggedRow key={c.id} idx={i + 1} card={c}
                              onApprove={(suggested) => openOverride(c.id, "approve", suggested)}
                              onReject={(suggested) => openOverride(c.id, "reject", suggested)}
                              onRequest={() => setResponseOpen(true)} />
                ))}
              </div>
            </div>
          )}

          {/* Auto-approved (collapsible) */}
          {approvedCards.length > 0 && (
            <div data-testid="auto-approved-section" className="bg-surface border border-line rounded-xl">
              <button
                type="button"
                onClick={() => setAutoExpanded(v => !v)}
                data-testid="auto-approved-toggle"
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-alt/40 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldCheck className="size-4 text-emerald-600 shrink-0" />
                  <span className="font-brand text-h3 text-ink font-semibold">{approvedCards.length} auto-approved</span>
                  <span className="text-caption text-ink-subtle">· {format.currency(totalApprovedFace)} · cleared by parser</span>
                </div>
                <ChevronDown className={`size-4 text-ink-muted transition-transform ${autoExpanded ? "rotate-180" : ""}`} />
              </button>
              {autoExpanded && (
                <div className="border-t border-line">
                  <div className="px-6 py-3 border-b border-line">
                    <div className="relative max-w-xs">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-ink-hint" />
                      <Input value={autoQ} onChange={(e) => setAutoQ(e.target.value)} placeholder="Search last-4 or amount…" className="h-8 pl-8 text-caption" data-testid="auto-search" />
                    </div>
                  </div>
                  {/* Sticky thead + max-h scroll for 200+ row scale */}
                  <div className="max-h-[420px] overflow-y-auto" data-testid="auto-table-scroll">
                    <table className="w-full text-body-sm">
                      <thead className="bg-surface-alt sticky top-0 z-[1]">
                        <tr className="text-left">
                          <th className="px-6 py-2.5 text-eyebrow uppercase text-ink-hint">#</th>
                          <th className="px-3 py-2.5 text-eyebrow uppercase text-ink-hint">Card</th>
                          <th className="px-3 py-2.5 text-eyebrow uppercase text-ink-hint">PIN</th>
                          <th className="px-3 py-2.5 text-eyebrow uppercase text-ink-hint text-right">Face</th>
                          <th className="px-6 py-2.5 text-eyebrow uppercase text-ink-hint text-right">Approved</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line/40">
                        {filteredAuto.map((c, i) => (
                          <tr key={c.id} className="hover:bg-surface-alt/30" data-testid={`auto-row-${c.id}`}>
                            <td className="px-6 py-2 text-ink-hint tabular-nums text-caption">{i + 1}</td>
                            <td className="px-3 py-2 font-mono text-code text-ink whitespace-nowrap">{format.cardLast4(c.cardNumber)}</td>
                            <td className="px-3 py-2 text-ink-muted text-caption">{c.pinPresent ? "✓" : "—"}</td>
                            <td className="px-3 py-2 text-ink tabular-nums text-right">{format.currency(c.faceValue)}</td>
                            <td className="px-6 py-2 text-emerald-700 text-caption text-right tabular-nums">{c.approvedAt ? format.relative(c.approvedAt) : "—"}</td>
                          </tr>
                        ))}
                        {filteredAuto.length === 0 && (
                          <tr><td colSpan={5} className="px-6 py-8 text-center text-caption text-ink-subtle">No matches.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rejected (informational only) */}
          {rejectedCards.length > 0 && (
            <details className="bg-surface border border-line rounded-xl">
              <summary className="px-6 py-3.5 cursor-pointer text-body-sm text-ink-muted flex items-center gap-2">
                <XCircle className="size-3.5 text-red-600" /> {rejectedCards.length} rejected · {format.currency(rejectedCards.reduce((s,c)=>s+c.faceValue,0))}
              </summary>
              <div className="border-t border-line divide-y divide-line/60">
                {rejectedCards.map((c) => (
                  <div key={c.id} className="px-6 py-3 text-caption flex justify-between">
                    <span className="font-mono text-code text-ink-muted">{format.cardLast4(c.cardNumber)}</span>
                    <span className="text-ink-muted truncate max-w-xs">{c.overrideReason || c.flagReason || "Rejected"}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>

        {/* RIGHT RAIL: context */}
        <aside className="lg:col-span-3 space-y-5">
          <div className="bg-surface border border-line rounded-xl p-5">
            <div className="eyebrow mb-3">Member context</div>
            <Link to={`/admin/members/${member?.id}`} className="flex items-center justify-between text-body-sm hover:bg-surface-alt rounded-md -m-1 p-1.5 transition-colors">
              <span className="text-ink font-medium">{member?.firstName} {member?.lastName}</span>
              <ChevronRight className="size-3.5 text-ink-hint" />
            </Link>
            <dl className="mt-3 space-y-1.5 text-caption">
              <Row label="Lifetime earned" value={format.currency(member?.lifetimeEarnings || 0)} />
              <Row label="Member since" value={format.date(member?.memberSince)} />
              <Row label="Bank" value={`${member?.bankName || ""} ${format.bankLast4(member?.accountLast4)}`} />
            </dl>
          </div>

          <div className="bg-surface border border-line rounded-xl">
            <div className="px-5 py-3.5 border-b border-line flex items-center justify-between">
              <h3 className="text-body-sm font-semibold text-ink">Original paste</h3>
              <button className="text-ink-hint hover:text-ink" title="Copy raw"
                      onClick={() => { try { navigator.clipboard.writeText(submission.rawText || ""); toast.success("Copied"); } catch { /* noop */ } }}>
                <Copy className="size-3.5" />
              </button>
            </div>
            <pre className="p-4 font-mono text-[11px] text-ink-muted whitespace-pre-wrap leading-relaxed max-h-72 overflow-auto">
{submission.rawText || "(no raw text on file)"}
            </pre>
            {submission.memberNote && (
              <div className="px-5 py-3 border-t border-line bg-amber-50/40">
                <div className="eyebrow text-amber-800">Member note</div>
                <p className="mt-1 text-caption text-ink-muted leading-relaxed">{submission.memberNote}</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Per-row override dialog */}
      <Dialog open={!!override} onOpenChange={(v) => !v && setOverride(null)}>
        <DialogContent data-testid="override-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-brand text-h2">
              {override?.kind === "approve" ? "Approve flagged card" : "Reject flagged card"}
            </DialogTitle>
            <DialogDescription className="pt-1 leading-relaxed">
              {override?.kind === "approve"
                ? "Override the parser flag and approve this card. The reason will be logged to the audit trail and the card will become eligible for payment."
                : "Reject this card permanently. The reason will be logged and the member will not be paid for it."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 my-2">
            <label className="text-caption font-semibold text-ink block">Reason</label>
            <Textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                      data-testid="override-reason-input"
                      placeholder={override?.kind === "approve"
                        ? "e.g. Member confirmed cards came from corporate spend — not a duplicate."
                        : "e.g. Member could not provide proof of purchase."}
                      className="min-h-[120px]" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverride(null)}>Cancel</Button>
            <Button onClick={confirmOverride} data-testid="confirm-override-btn"
                    className={override?.kind === "approve" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-red-600 hover:bg-red-700 text-white"}>
              {override?.kind === "approve" ? "Approve with override" : "Reject card"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request response modal */}
      <Dialog open={responseOpen} onOpenChange={setResponseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request response from member</DialogTitle>
            <DialogDescription>
              An email will be sent to {member?.firstName}. The submission will be paused until they reply.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={`Hi ${member?.firstName},\n\nWe noticed an issue with one or more cards in your recent submission. Could you double-check and reply with corrected details?\n\nThanks.`}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            className="min-h-[180px] font-mono text-code"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResponseOpen(false)}>Cancel</Button>
            <Button onClick={sendResponse} className="bg-brand-700 hover:bg-brand-800 text-white">Send and pause review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Mini({ label, value, accent, testid }) {
  const cls = accent === "success" ? "text-emerald-700" : accent === "flagged" ? "text-orange-600" : "text-ink";
  return (
    <div data-testid={testid} className="bg-surface border border-line rounded-xl p-5">
      <div className="text-caption text-ink-subtle">{label}</div>
      <div className={`mt-1 font-brand text-h1 font-bold tabular-nums leading-none ${cls}`}>{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-ink-subtle">{label}</dt>
      <dd className="text-ink truncate font-medium tabular-nums">{value}</dd>
    </div>
  );
}

// Expandable flagged-card row — shows the parser's reason, the matched
// duplicate (if any), and inline override actions with reason capture.
function FlaggedRow({ idx, card, onApprove, onReject, onRequest }) {
  const [open, setOpen] = useState(false);
  const dup = card.duplicateOfCardId ? getCardById(card.duplicateOfCardId) : null;
  const dupSub = dup ? getSubmissionById(dup.submissionId) : null;
  const dupMember = dup ? getMemberById(dup.memberId) : null;
  const flagTone = card.flagSeverity === "cross-member" ? "red"
                 : card.flagSeverity === "same-member"  ? "amber"
                 : card.flagSeverity === "intra-batch"  ? "amber"
                 : "orange";
  const toneClass = flagTone === "red"
    ? "bg-red-50 text-red-800 border-red-200"
    : flagTone === "amber"
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-orange-50 text-orange-800 border-orange-200";

  const suggestedApproveReason = card.flagSeverity === "intra-batch"
    ? "Confirmed legitimate split-balance pair — not a duplicate."
    : card.flagSeverity === "cross-member"
    ? "Confirmed via member call — both members have legitimate claim."
    : "Member confirmed — proceed.";

  return (
    <div data-testid={`flagged-row-${card.id}`} className="px-6 py-3.5">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-ink-hint tabular-nums text-caption w-6 shrink-0">{idx}</span>
        <span className="font-mono text-code text-ink whitespace-nowrap min-w-[100px]">{format.cardLast4(card.cardNumber)}</span>
        <span className="text-ink tabular-nums whitespace-nowrap">{format.currency(card.faceValue)}</span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-caption ${toneClass}`}>
          <AlertTriangle className="size-3" />
          <span className="truncate max-w-[280px]">{card.flagReason || card.status}</span>
        </span>
        <button onClick={() => setOpen(v => !v)} data-testid={`flagged-toggle-${card.id}`}
                className="ml-auto inline-flex items-center gap-1 text-caption text-ink-muted hover:text-ink">
          {open ? "Hide details" : "Why flagged?"} <ChevronDown className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div data-testid={`flagged-detail-${card.id}`} className="mt-3 ml-10 bg-surface-alt/60 rounded-lg p-4 space-y-3">
          <div>
            <div className="eyebrow text-ink-subtle">Parser rationale</div>
            <p className="mt-1 text-caption text-ink leading-relaxed">{card.flagReason || "Flagged by validation rules."}</p>
          </div>
          {dup && (
            <div className="border-t border-line/60 pt-3">
              <div className="eyebrow text-ink-subtle">Matched card</div>
              <div className="mt-1 flex items-center gap-2 text-caption">
                <span className="font-mono text-code text-ink">{format.cardLast4(dup.cardNumber)}</span>
                <span className="text-ink-muted">·</span>
                <span className="text-ink-muted">{dupMember?.firstName} {dupMember?.lastName}</span>
                {dupSub && <>
                  <span className="text-ink-muted">·</span>
                  <Link to={`/admin/submissions/${dupSub.id}`} className="text-brand-700 hover:underline">{dupSub.id}</Link>
                </>}
                <span className="text-ink-muted">·</span>
                <span className="text-ink-muted">{dup.approvedAt ? `approved ${format.relative(dup.approvedAt)}` : "pending"}</span>
              </div>
            </div>
          )}
          <div className="border-t border-line/60 pt-3 flex flex-wrap items-center gap-2">
            <Gated action="submissions.approve">
              <Button size="sm" onClick={() => onApprove(suggestedApproveReason)}
                      data-testid={`approve-flagged-${card.id}`}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <CheckCircle2 className="size-3.5 mr-1.5" /> Approve anyway
              </Button>
            </Gated>
            <Gated action="submissions.reject">
              <Button size="sm" variant="outline" onClick={() => onReject("Confirmed duplicate; member could not provide proof.")}
                      data-testid={`reject-flagged-${card.id}`}
                      className="border-red-200 text-red-700 hover:bg-red-50">
                <XCircle className="size-3.5 mr-1.5" /> Reject
              </Button>
            </Gated>
            <Gated action="submissions.request">
              <Button size="sm" variant="outline" onClick={onRequest}
                      data-testid={`request-response-flagged-${card.id}`}>
                <Flag className="size-3.5 mr-1.5" /> Ask member
              </Button>
            </Gated>
            {card.overrideReason && (
              <span className="ml-auto text-caption text-ink-muted italic truncate max-w-xs">
                Last override: {card.overrideReason}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
